import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, Edit2, Trash2, AlertTriangle } from 'lucide-react';
import { ChevronDown, Search, SortAsc, SortDesc, Calendar, Filter, X, Plus } from 'lucide-react';

// Import the JSON-based configuration
import appConfig, { ENDPOINT_KEYS } from './config/appConfig';

const DialogStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  },
  dialog: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    width: '400px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  title: {
    fontSize: '18px',
    textAlign: 'center',
    margin: 0,
    color: '#000',
    backgroundColor: 'white',
  },
  button: {
    backgroundColor: '#FFFDD0',
    border: 'none',
    borderRadius: '4px',
    padding: '10px',
    width: '100%',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 500,
    color: 'black'
  }
};

const Dialog = ({ message, onClose, onCancel }) => {
  const isConfirmation = message.startsWith('Are you sure');

  const formattedMessage = message.split('\n').map((line, i) => (
    <React.Fragment key={i}>
      {line}
      {i < message.split('\n').length - 1 && <br />}
    </React.Fragment>
  ));

  return (
    <div style={DialogStyles.overlay}>
      <div style={DialogStyles.dialog}>
        <h2 style={DialogStyles.title}>
          {formattedMessage}
        </h2>
        {isConfirmation ? (
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              style={{
                ...DialogStyles.button,
                backgroundColor: '#FF8F89',
                color: 'white',
                width: '50%'
              }}
              onClick={onClose}
            >
              Yes
            </button>
            <button
              style={{
                ...DialogStyles.button,
                width: '50%'
              }}
              onClick={onCancel}
            >
              No
            </button>
          </div>
        ) : (
          <button
            style={DialogStyles.button}
            onClick={onClose}
          >
            OK
          </button>
        )}
      </div>
    </div>
  );
};

const ManageRaidLogCommentsDialog = ({
  isOpen,
  onClose,
  selectedRaidLogs,
  corporateAccount,
  currentProject,
  login_token,
  raidLogType,
  raidLogIdWithPrefix
}) => {
  const [comments, setComments] = useState([]);
  const [selectedComments, setSelectedComments] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [commentForm, setCommentForm] = useState({
    comments: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [sortCriteria, setSortCriteria] = useState('created_date desc');
  const [isInitialized, setIsInitialized] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [filteredComments, setFilteredComments] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // New state for update and delete functionality
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingComment, setEditingComment] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [pendingDeleteAction, setPendingDeleteAction] = useState(null);

  // New state for improved delete confirmation dialog
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  // Permissions state
  const [permissions, setPermissions] = useState({
    canCreate: false,
    canEdit: false,
    canDelete: false
  });

  const textAreaRef = useRef(null);

  // Permissions check function
  const checkPermission = async (categoryHeader) => {
    try {
      const response = await fetch(appConfig.getUrl('/get_user_access_level_new'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          user_id: localStorage.getItem('user_id'),
          project_id: currentProject,
          category_header: categoryHeader,
          category_sub_header: ['Create or Edit Comments']
        }),
      });
      const data = await response.json();
      console.log('API Response:', data);

      const newPermissions = {
        canCreate: false,
        canEdit: false,
        canDelete: false
      };

      data.user_actions_list.forEach(item => {
        switch(item.user_action) {
          case 'Create or Edit Comments':
            // If there's a combined permission, set both
            newPermissions.canCreate = item.has_access;
            newPermissions.canEdit = item.has_access;
            newPermissions.canDelete = item.has_access;
            break;
        }
      });

      console.log('New Permissions:', newPermissions);
      setPermissions(newPermissions);

      return data.access_status && data.status === 'Success';
    } catch (error) {
      console.error(`Error checking permission for ${categoryHeader}:`, error);
      return false;
    }
  };

  // Helper function to ensure data is fully reset
  const resetAllData = () => {
    setComments([]);
    setSelectedComments(new Set());
    setError('');
    setSuccessMessage('');
    setIsLoading(false);
    setSearchTerm('');
    setFilteredComments([]);
    setCurrentPage(1);
  };

  const showErrorDialog = (message) => {
    setDialogMessage(message);
    setShowDialog(true);
  };

  const handleDialogClose = () => {
    setShowDialog(false);
    if (pendingDeleteAction) {
      pendingDeleteAction();
      setPendingDeleteAction(null);
    }
  };

  const handleDialogCancel = () => {
    setShowDialog(false);
    setPendingDeleteAction(null);
  };

  // Sorting functionality
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Apply sorting to comments
  const sortedComments = React.useMemo(() => {
    let sortableComments = [...filteredComments];
    if (sortConfig.key) {
      sortableComments.sort((a, b) => {
        let aVal = a[sortConfig.key] || '';
        let bVal = b[sortConfig.key] || '';

        // Handle date sorting
        if (sortConfig.key === 'created_date') {
          aVal = new Date(aVal);
          bVal = new Date(bVal);
        } else {
          aVal = aVal.toString().toLowerCase();
          bVal = bVal.toString().toLowerCase();
        }

        if (aVal < bVal) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableComments;
  }, [filteredComments, sortConfig]);

  // Pagination
  const totalPages = Math.ceil((sortedComments?.length || 0) / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedComments = (sortedComments || []).slice(startIndex, startIndex + itemsPerPage);

  // Initialize component when dialog opens
  useEffect(() => {
    const initializeDialog = async () => {
      if (isOpen && !isInitialized) {
        // Reset all data
        resetAllData();
        setIsLoading(true);
        setSearchTerm('');

        // Check permissions first
        await checkPermission('Manage Comments');

        // Fetch comments
        await fetchComments('', raidLogType, '');

        setIsInitialized(true);
      } else if (!isOpen) {
        // Reset initialization when dialog closes
        setIsInitialized(false);
      }
    };

    initializeDialog();
  }, [isOpen, raidLogType]);

  // Effect to handle search term changes and filtering
  useEffect(() => {
    let filtered = comments;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(comment =>
        comment.comments.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (comment.user_name || comment.user_id).toLowerCase().includes(searchTerm.toLowerCase()) ||
        (comment.raid_id_with_prefix || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredComments(filtered);
    setCurrentPage(1); // Reset to first page when filtering
  }, [searchTerm, comments]);

  const fetchComments = async (statusList = [], raidLogTypeParam = raidLogType, searchTermParam = null) => {
    const searchTermToUse = searchTermParam !== null ? searchTermParam : searchTerm;

    setComments([]);

    if (!isLoading) {
      setIsLoading(true);
    }

    // Determine parameters based on level
    let raidLogId = null;
    raidLogId = Array.from(selectedRaidLogs)[0];

    try {
      const response = await fetch(appConfig.getUrl('/get_comments_list'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          level_id: '',
          req_type: raidLogType,
          req_id: raidLogId || '',
          filter_by_status: [], // Pass empty array since we don't filter by status
          search_query: searchTermToUse,
          sort_criteria: sortCriteria
        })
      });

      const data = await response.json();

      if (data.status === 'Success') {
        setComments(data.comments_list || []);
        setFilteredComments(data.comments_list || []);
      } else {
        throw new Error(data.status_description || 'Failed to fetch comments');
      }
    } catch (err) {
      setComments([]);
      setFilteredComments([]);
      setError(err.message);
      console.error('Error fetching comments:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenCreateDialog = () => {
    if (!permissions.canCreate) {
      setError('You do not have permission to create comments');
      return;
    }

    setCommentForm({
      comments: ''
    });
    setError('');
    setIsCreateDialogOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCommentForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearchInputChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSearch = () => {
    // Search is handled by useEffect
  };

  const handleCreateComment = async () => {
    if (!permissions.canCreate) {
      setError('You do not have permission to create comments');
      return;
    }

    setIsLoading(true);
    setError('');

    // Validate input
    if (!commentForm.comments) {
      setError('Please enter a comment');
      setIsLoading(false);
      return;
    }

    let raidLogIds = [];
    raidLogIds = Array.from(selectedRaidLogs);

    try {
      const userId = localStorage.getItem('user_id') || '';

      const response = await fetch(appConfig.getUrl('/add_comments'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          level_id: '',
          req_type: raidLogType,
          req_ids: raidLogIds,
          comments: commentForm.comments,
          status: 'Active', // Always pass 'Active' as status
          user_id: userId
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        setSuccessMessage(data.status_description || 'Comment created successfully');

        // First refresh the comments list
        await fetchComments();

        // Then close the dialog and clear selections
        setIsCreateDialogOpen(false);
        setCommentForm({
          comments: ''
        });

        // Clear success message after longer delay to ensure user sees it
        setTimeout(() => {
          setSuccessMessage('');
        }, 5000);
      } else {
        throw new Error(data.status_description || 'Failed to create comment');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error creating comment:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // New function to handle opening the edit dialog
  const handleOpenEditDialog = (comment) => {
    if (!permissions.canEdit) {
      setError('You do not have permission to edit comments');
      return;
    }

    setEditingComment(comment);
    setCommentForm({
      comments: comment.comments
    });
    setError('');
    setIsEditDialogOpen(true);
  };

  // New function to handle updating a comment
  const handleUpdateComment = async () => {
    if (!permissions.canEdit) {
      setError('You do not have permission to edit comments');
      return;
    }

    setIsLoading(true);
    setError('');

    // Validate input
    if (!commentForm.comments) {
      setError('Please enter a comment');
      setIsLoading(false);
      return;
    }

    try {
      const userId = localStorage.getItem('user_id') || '';

      const response = await fetch(appConfig.getUrl('/update_comments'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + login_token
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          comment_id: editingComment.comment_id,
          comments: commentForm.comments,
          status: 'Active', // Always pass 'Active' as status
          user_id: userId
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        setSuccessMessage(data.status_description || 'Comment updated successfully');

        // First refresh the comments list
        await fetchComments();

        // Then close the dialog and clear selections
        setIsEditDialogOpen(false);
        setEditingComment(null);
        setCommentForm({
          comments: ''
        });

        // Clear success message after longer delay to ensure user sees it
        setTimeout(() => {
          setSuccessMessage('');
        }, 5000);
      } else {
        throw new Error(data.status_description || 'Failed to update comment');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error updating comment:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Updated delete confirmation handler
  const handleDeleteSelectedConfirm = () => {
    if (!permissions.canDelete) {
      setError('You do not have permission to delete comments');
      return;
    }

    if (selectedComments.size === 0) {
      setError('No comments selected for deletion');
      return;
    }
    setIsDeleteConfirmOpen(true);
  };

  // Updated delete function
  const handleDelete = async () => {
    if (!permissions.canDelete) {
      setError('You do not have permission to delete comments');
      return;
    }

    setIsDeleteConfirmOpen(false);
    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await fetch(appConfig.getUrl('/delete_comments'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + login_token
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          comment_ids: Array.from(selectedComments)
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        setSuccessMessage(`Successfully deleted ${selectedComments.size} comment(s)`);
        setSelectedComments(new Set());

        // Refresh the comments list
        await fetchComments();

        // Clear success message after longer delay to ensure user sees it
        setTimeout(() => {
          setSuccessMessage('');
        }, 5000);
      } else {
        throw new Error(data.status_description || 'Failed to delete comments');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error deleting comments:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // New improved delete confirmation dialog
  const renderDeleteConfirmDialog = () => {
    if (!isDeleteConfirmOpen) return null;

    return (
      <div style={{...DialogStyles.overlay, zIndex: 1100}}>
        <div style={{...DialogStyles.dialog, width: '500px', padding: '0'}}>
          <div style={{
            backgroundColor: '#FFE6E6',
            color: '#CC0000',
            padding: '15px 20px',
            borderRadius: '8px 8px 0 0',
            textAlign: 'center',
            fontSize: '18px',
            fontWeight: 'bold'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              <AlertTriangle size={20} />
              <span>Confirm Deletion</span>
            </div>
          </div>

          <div style={{ padding: '20px' }}>
            <p style={{ color: '#6b7280', marginBottom: '16px', fontSize: '14px' }}>
              Are you sure you want to delete {selectedComments.size} selected comment{selectedComments.size !== 1 ? 's' : ''}?
            </p>
            <p style={{ fontSize: '12px', color: '#dc2626', marginBottom: '16px' }}>
              This action cannot be undone.
            </p>

            {/* Show some details about selected comments */}
            <div style={{
              backgroundColor: '#f9fafb',
              borderRadius: '6px',
              padding: '12px',
              marginBottom: '16px',
              maxHeight: '120px',
              overflowY: 'auto',
              border: '1px solid #e5e7eb'
            }}>
              <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>Selected comments:</p>
              {Array.from(selectedComments).slice(0, 5).map(commentId => {
                const comment = comments.find(c => c.comment_id === commentId);
                return comment ? (
                  <div key={commentId} style={{ fontSize: '12px', color: '#374151', marginBottom: '4px' }}>
                    <span style={{
                      padding: '1px 6px',
                      borderRadius: '8px',
                      fontSize: '10px',
                      backgroundColor: '#e0e7ff',
                      color: '#3730a3',
                      marginRight: '6px'
                    }}>
                      {comment.user_name || comment.user_id}
                    </span>
                    {comment.comments.length > 50 ? comment.comments.substring(0, 50) + '...' : comment.comments}
                  </div>
                ) : null;
              })}
              {selectedComments.size > 5 && (
                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                  ... and {selectedComments.size - 5} more
                </div>
              )}
            </div>
          </div>

          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '16px',
            padding: '0 20px 20px 20px'
          }}>
            <button
              onClick={() => setIsDeleteConfirmOpen(false)}
              style={{
                backgroundColor: '#f8f9fa',
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              style={{
                backgroundColor: '#ef4444',
                color: 'white',
                padding: '8px 16px',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Delete {selectedComments.size} Comment{selectedComments.size !== 1 ? 's' : ''}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const toggleSelectAll = () => {
    if (selectedComments.size === paginatedComments.length) {
      setSelectedComments(new Set());
    } else {
      const newSelected = new Set(paginatedComments.map(comment => comment.comment_id));
      setSelectedComments(newSelected);
    }
  };

  const toggleRowSelection = (commentId) => {
    const newSelected = new Set(selectedComments);
    if (newSelected.has(commentId)) {
      newSelected.delete(commentId);
    } else {
      newSelected.add(commentId);
    }
    setSelectedComments(newSelected);
  };

  useEffect(() => {
    if (isCreateDialogOpen && textAreaRef.current) {
      textAreaRef.current.focus();
    }
  }, [isCreateDialogOpen]);

  // Create Comment Dialog Content
  const renderCommentFormDialog = () => {
    if (!isCreateDialogOpen) return null;

    return (
      <div style={DialogStyles.overlay}>
        <div style={{...DialogStyles.dialog, width: '700px'}}>
          <h2 style={{ ...DialogStyles.title, backgroundColor: '#CCCCFF' }}>Create New Comment</h2>

          {/* Level Information */}
          <div className="mb-6 p-4 border border-gray-300 rounded-md">
            <h3 className="text-base font-semibold mb-3">Adding comment to:</h3>
            <p>Raid Log: <strong>{raidLogIdWithPrefix}</strong></p>
          </div>

          <form onSubmit={(e) => { e.preventDefault(); handleCreateComment(); }}>
            {/* Comment Form */}
            <div style={{marginBottom: '15px'}}>
              <label style={{display: 'block', marginBottom: '5px'}}>
                Comment: <span className="text-red-500">*</span>
              </label>
              <textarea
                name="comments"
                value={commentForm.comments}
                ref={textAreaRef}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  height: '96px'
                }}
                placeholder="Enter your comment"
                disabled={isLoading}
                required
              />
            </div>

            {error && (
              <div className={'mb-4 p-2 rounded ' + (
                error.includes('successfully')
                  ? 'bg-green-50 text-green-700'
                  : 'bg-red-50 text-red-500'
              )}>
                {error}
              </div>
            )}

            <div className="flex justify-center gap-4">
              <button
                type="button"
                onClick={() => {
                  setIsCreateDialogOpen(false);
                  setCommentForm({
                    comments: ''
                  });
                  setError('');
                }}
                style={{
                  backgroundColor: '#FFFDD0',
                  padding: '8px 16px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                disabled={isLoading}
              >
                Close
              </button>

              <button
                type="submit"
                style={{
                  padding: '8px 16px',
                  backgroundColor: permissions.canCreate ? '#C8F4DE' : '#CCCCCC',
                  color: 'black',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: permissions.canCreate ? 'pointer' : 'not-allowed'
                }}
                disabled={isLoading || !permissions.canCreate}
              >
                {isLoading ? 'Processing...' : 'Create Comment'}
              </button>
            </div>

            {!isLoading && (
              <p className="text-gray-600 text-sm mt-4 text-center">
                You can continue adding more comments. Click 'Close' when finished.
              </p>
            )}
          </form>
        </div>
      </div>
    );
  };

  // Edit Comment Dialog Content
  const renderEditDialog = () => {
    if (!isEditDialogOpen || !editingComment) return null;

    return (
      <div style={DialogStyles.overlay}>
        <div style={{...DialogStyles.dialog, width: '700px'}}>
          <h2 style={{ ...DialogStyles.title, backgroundColor: '#CCCCFF' }}>Edit Comment</h2>

          <form onSubmit={(e) => { e.preventDefault(); handleUpdateComment(); }}>
            {/* Comment Form */}
            <div style={{marginBottom: '15px'}}>
              <label style={{display: 'block', marginBottom: '5px'}}>
                Comment: <span className="text-red-500">*</span>
              </label>
              <textarea
                name="comments"
                value={commentForm.comments}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  height: '96px'
                }}
                placeholder="Enter your comment"
                disabled={isLoading}
                required
              />
            </div>

            {error && (
              <div className={'mb-4 p-2 rounded ' + (
                error.includes('successfully')
                  ? 'bg-green-50 text-green-700'
                  : 'bg-red-50 text-red-500'
              )}>
                {error}
              </div>
            )}

            <div className="flex justify-center gap-4">
              <button
                type="button"
                onClick={() => {
                  setIsEditDialogOpen(false);
                  setEditingComment(null);
                  setCommentForm({
                    comments: ''
                  });
                  setError('');
                }}
                style={{
                  backgroundColor: '#FFFDD0',
                  padding: '8px 16px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                disabled={isLoading}
              >
                Close
              </button>

              <button
                type="submit"
                style={{
                  padding: '8px 16px',
                  backgroundColor: permissions.canEdit ? '#C8F4DE' : '#CCCCCC',
                  color: 'black',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: permissions.canEdit ? 'pointer' : 'not-allowed'
                }}
                disabled={isLoading || !permissions.canEdit}
              >
                {isLoading ? 'Processing...' : 'Update Comment'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        width: '95vw',
        maxWidth: '1400px',
        minHeight: '400px',
        maxHeight: '90vh',
        height: 'auto',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>

        {/* Header with title and search */}
        <div style={{
          backgroundColor: '#c8d7f0',
          padding: '12px 20px',
          borderRadius: '12px 12px 0 0',
          display: 'flex',
          alignItems: 'center',
          gap: '20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input
              type="text"
              placeholder="Search comments..."
              value={searchTerm}
              onChange={handleSearchInputChange}
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px',
                width: '250px'
              }}
            />
            <button
              onClick={handleSearch}
              style={{
                padding: '8px 12px',
                backgroundColor: '#4285f4',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <Search size={16} />
            </button>
          </div>

          <h1 style={{
            flex: 1,
            textAlign: 'center',
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#4285f4',
            margin: 0
          }}>
            Manage RAID Log Comments - {raidLogIdWithPrefix}
          </h1>

          {/* Create Comment Button with permissions and tooltip */}
          <div className="group relative">
            <button
              onClick={handleOpenCreateDialog}
              disabled={!permissions.canCreate}
              style={{
                padding: '8px 16px',
                backgroundColor: permissions.canCreate ? '#4285f4' : '#93aded',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                opacity: permissions.canCreate ? 1 : 0.6
              }}
            >
              <Plus size={16} />
              Create New Comment
            </button>

            {/* Hover tooltip for disabled state */}
            {!permissions.canCreate && (
              <div className="absolute right-0 top-full mt-2 px-3 py-2 bg-gray-600 text-white text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                Insufficient permission to create comments
                <div className="absolute bottom-full right-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-600"></div>
              </div>
            )}
          </div>
        </div>

        {/* Simple Header Bar */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#f8f9fa',
          display: 'flex',
          alignItems: 'center',
          gap: '16px'
        }}>
          <span style={{ fontSize: '14px', fontWeight: '600' }}>RAID Log Comments</span>
        </div>

        {/* Error/Success Messages */}
        {(error || successMessage) && (
          <div style={{ padding: '12px 20px' }}>
            {error && (
              <div style={{
                padding: '8px 12px',
                backgroundColor: error.includes('successfully') ? '#d4edda' : '#f8d7da',
                color: error.includes('successfully') ? '#155724' : '#721c24',
                border: `1px solid ${error.includes('successfully') ? '#c3e6cb' : '#f5c6cb'}`,
                borderRadius: '4px',
                marginBottom: '8px'
              }}>
                {error}
              </div>
            )}

            {successMessage && (
              <div style={{
                padding: '8px 12px',
                backgroundColor: '#d4edda',
                color: '#155724',
                border: '1px solid #c3e6cb',
                borderRadius: '4px'
              }}>
                {successMessage}
              </div>
            )}
          </div>
        )}

        {/* Table Container */}
        <div style={{ flex: 1, overflow: 'auto', padding: '0 20px' , maxHeight: 'calc(90vh - 200px)' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '14px'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th style={{
                  padding: '12px 8px',
                  textAlign: 'left',
                  borderBottom: '2px solid #dee2e6',
                  color: '#4285f4',
                  fontWeight: '600',
                  fontSize: '14px'
                }}>
                  <input
                    type="checkbox"
                    checked={paginatedComments.length > 0 && selectedComments.size === paginatedComments.length}
                    onChange={toggleSelectAll}
                    disabled={paginatedComments.length === 0}
                    style={{ marginRight: '8px' }}
                  />
                  Select All
                </th>
                <th style={{
                  padding: '12px 8px',
                  textAlign: 'left',
                  borderBottom: '2px solid #dee2e6',
                  color: '#4285f4',
                  fontWeight: '600',
                  fontSize: '14px',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
                onClick={() => handleSort('raid_id_with_prefix')}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    RAID ID
                    {sortConfig.key === 'raid_id_with_prefix' && (
                      <span style={{ marginLeft: '4px' }}>
                        {sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />}
                      </span>
                    )}
                  </div>
                </th>
                <th style={{
                  padding: '12px 8px',
                  textAlign: 'left',
                  borderBottom: '2px solid #dee2e6',
                  color: '#4285f4',
                  fontWeight: '600',
                  fontSize: '14px',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
                onClick={() => handleSort('created_date')}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    Comment Date/Time
                    {sortConfig.key === 'created_date' && (
                      <span style={{ marginLeft: '4px' }}>
                        {sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />}
                      </span>
                    )}
                  </div>
                </th>
                <th style={{
                  padding: '12px 8px',
                  textAlign: 'left',
                  borderBottom: '2px solid #dee2e6',
                  color: '#4285f4',
                  fontWeight: '600',
                  fontSize: '14px',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
                onClick={() => handleSort('user_name')}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    Logged By User
                    {sortConfig.key === 'user_name' && (
                      <span style={{ marginLeft: '4px' }}>
                        {sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />}
                      </span>
                    )}
                  </div>
                </th>

                <th style={{
                  padding: '12px 8px',
                  textAlign: 'left',
                  borderBottom: '2px solid #dee2e6',
                  color: '#4285f4',
                  fontWeight: '600',
                  fontSize: '14px'
                }}>
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="5" style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                    Loading...
                  </td>
                </tr>
              ) : paginatedComments.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                    No comments found. Create a new comment or adjust your search.
                  </td>
                </tr>
              ) : (
                paginatedComments.map((comment, index) => (
                  <React.Fragment key={comment.comment_id}>
                    {/* First row with main data */}
                    <tr
                      style={{
                        backgroundColor: selectedComments.has(comment.comment_id) ? '#e3f2fd' : (index % 2 === 0 ? '#fafafa' : 'white'),
                        borderBottom: 'none'
                      }}
                    >
                      <td style={{ padding: '8px 8px 4px 8px' }}>
                        <input
                          type="checkbox"
                          checked={selectedComments.has(comment.comment_id)}
                          onChange={() => toggleRowSelection(comment.comment_id)}
                        />
                      </td>
                      <td style={{ padding: '8px 8px 4px 8px', fontSize: '12px' }}>
                        {comment.raid_id_with_prefix}
                      </td>
                      <td style={{ padding: '8px 8px 4px 8px', fontSize: '12px' }}>
                        {new Date(comment.created_date).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '8px 8px 4px 8px', fontSize: '12px' }}>
                        {comment.user_name || comment.user_id}
                      </td>
                      <td style={{ padding: '8px 8px 4px 8px' }}>
                        <div className="group relative">
                          <button
                            onClick={() => handleOpenEditDialog(comment)}
                            disabled={!permissions.canEdit}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: permissions.canEdit ? '#4285f4' : '#cccccc',
                              cursor: permissions.canEdit ? 'pointer' : 'not-allowed',
                              padding: '4px',
                              opacity: permissions.canEdit ? 1 : 0.6
                            }}
                            title="Edit"
                          >
                            <Edit2 size={16} />
                          </button>

                          {/* Tooltip for edit button when disabled */}
                          {!permissions.canEdit && (
                            <div className="absolute left-0 bottom-full mb-2 px-2 py-1 bg-gray-600 text-white text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                              Insufficient permission to edit
                              <div className="absolute top-full left-4 w-0 h-0 border-l-2 border-r-2 border-t-2 border-transparent border-t-gray-600"></div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                    {/* Second row with comment */}
                    <tr
                      style={{
                        backgroundColor: selectedComments.has(comment.comment_id) ? '#e3f2fd' : (index % 2 === 0 ? '#fafafa' : 'white'),
                        borderBottom: '1px solid #dee2e6'
                      }}
                    >
                      <td style={{ padding: '4px 8px 8px 8px' }}></td>
                      <td colSpan="4" style={{
                        padding: '4px 8px 8px 8px',
                        fontSize: '12px',
                        fontStyle: 'italic',
                        color: '#555'
                      }}>
                        <div style={{ wordWrap: 'break-word', lineHeight: '1.4', color: 'purple' }}>
                          <strong>Comment:</strong> {comment.comments}
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!isLoading && sortedComments.length > 0 && (
          <div style={{
            padding: '12px 20px',
            borderTop: '1px solid #dee2e6',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#f8f9fa',
            fontSize: '14px'
          }}>
            <div style={{ color: '#666' }}>
              Total Entries: {sortedComments.length} | Showing {sortedComments.length > 0 ? startIndex + 1 : 0} - {Math.min(startIndex + itemsPerPage, sortedComments.length)} of {sortedComments.length}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                style={{
                  padding: '6px',
                  border: 'none',
                  backgroundColor: 'transparent',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  opacity: currentPage === 1 ? 0.5 : 1,
                  color: '#4285f4'
                }}
              >
                ‹
              </button>
              <span style={{ color: '#4285f4' }}>
                Page {currentPage} of {Math.max(1, totalPages)}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages || totalPages === 0}
                style={{
                  padding: '6px',
                  border: 'none',
                  backgroundColor: 'transparent',
                  cursor: currentPage === totalPages || totalPages === 0 ? 'not-allowed' : 'pointer',
                  opacity: currentPage === totalPages || totalPages === 0 ? 0.5 : 1,
                  color: '#4285f4'
                }}
              >
                ›
              </button>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>Items per page:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                style={{
                  padding: '4px 8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{
          padding: '12px 20px',
          borderTop: '1px solid #dee2e6',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#f8f9fa'
        }}>
          <div className="group relative">
            <button
              onClick={handleDeleteSelectedConfirm}
              disabled={isLoading || selectedComments.size === 0 || !permissions.canDelete}
              style={{
                padding: '8px 16px',
                backgroundColor: (selectedComments.size > 0 && !isLoading && permissions.canDelete) ? '#dc3545' : '#ff91ad',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '14px',
                opacity: (selectedComments.size > 0 && !isLoading && permissions.canDelete) ? 1 : 0.6
              }}
            >
              {isLoading ? 'Deleting...' : `Remove Selected (${selectedComments.size})`}
            </button>

            {/* Tooltip for delete button when disabled */}
            {(selectedComments.size === 0 || !permissions.canDelete) && (
              <div className="absolute left-0 bottom-full mb-2 px-3 py-2 bg-gray-600 text-white text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                {selectedComments.size === 0
                  ? "Select one or more comments to delete"
                  : "Insufficient permission to delete comments"
                }
                <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-600"></div>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {selectedComments.size > 0 && (
              <span style={{ fontSize: '14px', color: '#666' }}>
                {selectedComments.size} selected
              </span>
            )}
            <button
              onClick={onClose}
              style={{
                padding: '8px 16px',
                backgroundColor: '#FFFDD0',
                color: '#333',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Dialogs */}
      {showDialog && (
        <Dialog
          message={dialogMessage}
          onClose={handleDialogClose}
          onCancel={handleDialogCancel}
        />
      )}

      {/* Improved Delete Confirmation Dialog */}
      {renderDeleteConfirmDialog()}

      {/* Create Comment Dialog */}
      {renderCommentFormDialog()}

      {/* Edit Comment Dialog */}
      {renderEditDialog()}
    </div>
  );
};

export default ManageRaidLogCommentsDialog;